import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Eye, EyeOff } from "lucide-react";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });

const EMPTY_NOTE = { title: "", slug: "", tag: "Field Notes", excerpt: "", image: "", body: "", published: true };

const fieldCls =
  "w-full border border-ivory/20 bg-charcoal/60 px-4 py-3 text-sm text-ivory placeholder:text-ivory/35 focus:border-brass focus:outline-none transition-colors duration-300";

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const fmtError = (d) =>
  typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg || "").join(" ") : "Something went wrong";

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [notes, setNotes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadNotes = () =>
    client.get("/admin/journal").then((res) => setNotes(res.data)).catch(() => {});

  useEffect(() => {
    client
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        loadNotes();
      })
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await client.post("/auth/login", loginForm);
      setUser(data);
      loadNotes();
    } catch (err) {
      setLoginError(fmtError(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await client.post("/auth/logout").catch(() => {});
    setUser(false);
    setNotes([]);
    setEditing(null);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await client.put(`/admin/journal/${editing.id}`, editing);
      else await client.post("/admin/journal", editing);
      toast.success(editing.id ? "Field note updated." : "Field note published.");
      setEditing(null);
      loadNotes();
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (note) => {
    if (!window.confirm(`Delete "${note.title}"? This cannot be undone.`)) return;
    try {
      await client.delete(`/admin/journal/${note.id}`);
      toast.success("Field note deleted.");
      loadNotes();
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail));
    }
  };

  if (checking) return <><Seo title="Journal Admin | The Jamoon" description="The Jamoon journal administration." path="/admin" noindex /><main className="min-h-screen" data-testid="admin-loading" /></>;

  if (!user) {
    return (
      <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center px-6 pt-20">
        <Seo title="Journal Admin | The Jamoon" description="The Jamoon journal administration." path="/admin" noindex />
        <form onSubmit={login} className="w-full max-w-sm border border-brass/25 bg-forest p-8" data-testid="admin-login-form">
          <img src="/logo-full.png" alt="JAMOON" className="h-14 w-auto" />
          <h1 className="mt-6 font-serif text-2xl font-light text-ivory">Journal admin</h1>
          <p className="mt-2 text-xs text-ivory/50">For the lodge team only.</p>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={loginForm.email}
            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
            placeholder="Email"
            className={`${fieldCls} mt-6`}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={loginForm.password}
            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            placeholder="Password"
            className={`${fieldCls} mt-3`}
          />
          {loginError && (
            <p data-testid="admin-login-error" className="mt-3 text-xs text-red-400">{loginError}</p>
          )}
          <button
            data-testid="admin-login-btn"
            type="submit"
            className="mt-6 w-full bg-brass py-3 text-xs font-semibold tracking-[0.25em] text-charcoal transition-colors duration-300 hover:bg-[#B89B6A]"
          >
            SIGN IN
          </button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="mx-auto max-w-5xl px-6 pb-24 pt-32 sm:px-12">
      <Seo title="Journal Admin | The Jamoon" description="Manage The Jamoon field notes." path="/admin" noindex />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">Journal CMS</p>
          <h1 className="mt-3 font-serif text-3xl font-light text-ivory sm:text-4xl">Field notes</h1>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-new-note-btn"
            onClick={() => setEditing({ ...EMPTY_NOTE })}
            className="flex items-center gap-2 bg-brass px-5 py-2.5 text-[11px] font-semibold tracking-[0.2em] text-charcoal transition-colors duration-300 hover:bg-[#B89B6A]"
          >
            <Plus className="h-4 w-4" /> NEW NOTE
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 border border-ivory/20 px-5 py-2.5 text-[11px] tracking-[0.2em] text-ivory/70 transition-colors duration-300 hover:border-brass hover:text-brass"
          >
            <LogOut className="h-4 w-4" /> SIGN OUT
          </button>
        </div>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-10 border border-brass/30 bg-forest p-6 sm:p-8" data-testid="admin-note-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <input
              data-testid="note-title-input"
              required
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.id ? editing.slug : slugify(e.target.value) })}
              placeholder="Title"
              className={fieldCls}
            />
            <input
              data-testid="note-slug-input"
              required
              value={editing.slug}
              onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
              placeholder="url-slug"
              className={fieldCls}
            />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <input
              data-testid="note-tag-input"
              value={editing.tag}
              onChange={(e) => setEditing({ ...editing, tag: e.target.value })}
              placeholder="Tag (e.g. Field Notes)"
              className={fieldCls}
            />
            <input
              data-testid="note-image-input"
              value={editing.image}
              onChange={(e) => setEditing({ ...editing, image: e.target.value })}
              placeholder="Image — wix uri (ddf3da_...) or full https URL"
              className={fieldCls}
            />
          </div>
          <input
            data-testid="note-excerpt-input"
            value={editing.excerpt}
            onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
            placeholder="Excerpt — one line shown on the journal index"
            className={`${fieldCls} mt-4`}
          />
          <textarea
            data-testid="note-body-textarea"
            rows={10}
            value={editing.body}
            onChange={(e) => setEditing({ ...editing, body: e.target.value })}
            placeholder="The note itself. Separate paragraphs with a blank line."
            className={`${fieldCls} mt-4`}
          />
          <label className="mt-4 flex items-center gap-3 text-sm text-ivory/70">
            <input
              data-testid="note-published-checkbox"
              type="checkbox"
              checked={editing.published}
              onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
              className="h-4 w-4 accent-[#A58A5B]"
            />
            Published (visible on the site)
          </label>
          <div className="mt-6 flex gap-3">
            <button
              data-testid="note-save-btn"
              type="submit"
              disabled={saving}
              className="bg-brass px-7 py-3 text-xs font-semibold tracking-[0.25em] text-charcoal transition-colors duration-300 hover:bg-[#B89B6A] disabled:opacity-60"
            >
              {saving ? "SAVING…" : editing.id ? "SAVE CHANGES" : "PUBLISH NOTE"}
            </button>
            <button
              data-testid="note-cancel-btn"
              type="button"
              onClick={() => setEditing(null)}
              className="border border-ivory/20 px-7 py-3 text-xs tracking-[0.25em] text-ivory/70 transition-colors duration-300 hover:border-brass hover:text-brass"
            >
              CANCEL
            </button>
          </div>
        </form>
      )}

      <div className="mt-10">
        {notes.length === 0 && !editing && (
          <p className="border border-ivory/10 p-8 text-sm text-ivory/50" data-testid="admin-empty">
            No field notes yet. Write the first one.
          </p>
        )}
        {notes.map((n) => (
          <div
            key={n.id}
            data-testid={`admin-note-row-${n.slug}`}
            className="flex flex-wrap items-center justify-between gap-4 border-t border-ivory/10 py-5 last:border-b"
          >
            <div>
              <p className="font-serif text-xl font-light text-ivory">{n.title}</p>
              <p className="mt-1 text-xs text-ivory/45">
                /journal/{n.slug} · {n.tag}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span
                data-testid={`admin-note-status-${n.slug}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] ${
                  n.published ? "bg-brass/15 text-brass" : "bg-ivory/10 text-ivory/50"
                }`}
              >
                {n.published ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                {n.published ? "LIVE" : "DRAFT"}
              </span>
              <button
                data-testid={`admin-edit-${n.slug}`}
                onClick={() => setEditing({ ...EMPTY_NOTE, ...n })}
                className="border border-ivory/20 p-2.5 text-ivory/70 transition-colors duration-300 hover:border-brass hover:text-brass"
                aria-label={`Edit ${n.title}`}
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                data-testid={`admin-delete-${n.slug}`}
                onClick={() => remove(n)}
                className="border border-ivory/20 p-2.5 text-ivory/70 transition-colors duration-300 hover:border-red-400 hover:text-red-400"
                aria-label={`Delete ${n.title}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
